import React, { useState, useEffect } from 'react';
import { Users, MousePointerClick, Eye, TrendingUp, Radio } from 'lucide-react';
import { authService } from '../../services/auth';

const CHANNEL_LABELS = {
  'Direct': 'Directo',
  'Organic Search': 'Búsqueda orgánica',
  'Paid Search': 'Búsqueda paga',
  'Organic Social': 'Redes sociales',
  'Paid Social': 'Redes sociales (pago)',
  'Referral': 'Referidos',
  'Email': 'Email',
  'Unassigned': 'Sin asignar'
};

const ADMIN_PAGE_LABELS = {
  '/admin': 'Panel: Inicio',
  '/admin/properties': 'Admin Panel',
  '/admin/properties/new': 'Panel: Nueva propiedad',
  '/admin/reports': 'Panel: Reportes',
  '/admin/profile': 'Panel: Mi perfil',
  '/admin/appointments': 'Panel: Citas',
  '/admin/messages': 'Panel: Mensajes de contacto',
  '/admin/settings': 'Panel: Configuración',
  '/admin/users': 'Panel: Usuarios',
  '/admin/agents': 'Panel: Agentes',
  '/admin/login': 'Panel: Inicio de sesión',
  '/admin/verify': 'Panel: Verificación de cuenta'
};

// Convierte rutas técnicas (/admin/properties/edit/4, /propiedad/7) en algo entendible
const humanizePagePath = (path) => {
  if (!path) return 'Página desconocida';
  if (path === '/') return 'Inicio (página principal)';
  if (ADMIN_PAGE_LABELS[path]) return ADMIN_PAGE_LABELS[path];

  const editMatch = path.match(/^\/admin\/properties\/edit\/(\d+)/);
  if (editMatch) return `Panel: Editar propiedad #${editMatch[1]}`;

  const propertyMatch = path.match(/^\/propiedad\/(\d+)/);
  if (propertyMatch) return `Ficha de propiedad #${propertyMatch[1]}`;

  if (path.startsWith('/admin')) return 'Panel de administración';

  return path;
};

// GA4 devuelve la fecha como "20260707" (sin separadores)
const formatShortDate = (raw) => {
  if (!raw || raw.length !== 8) return raw;
  const iso = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};

const ReportsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realtime, setRealtime] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);

  useEffect(() => {
    loadSummary();
    loadRealtime();

    const interval = setInterval(loadRealtime, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/site-stats/summary`, {
        headers: authService.getAuthHeaders()
      });
      const result = await response.json();

      if (!response.ok) throw new Error(result.error || 'Error al cargar los reportes');

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRealtime = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/site-stats/realtime`, {
        headers: authService.getAuthHeaders()
      });
      const result = await response.json();
      if (response.ok) setRealtime(result);
    } catch (err) {
      // silencioso: no molestamos si falla el auto-refresh en tiempo real
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Reportes</h1>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">{error}</div>
      </div>
    );
  }

  const maxDayViews = Math.max(1, ...data.byDay.map((d) => d.pageViews));
  const labelStep = Math.max(1, Math.ceil(data.byDay.length / 7));
  const chartMinWidth = Math.max(data.byDay.length * 28, 480);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Reportes</h1>
        <p className="text-gray-600">Visitas a la web en los últimos 30 días (Google Analytics)</p>
      </div>

      {/* Tiempo real */}
      {realtime && (
        <div className="bg-gray-900 rounded-xl shadow p-6 mb-8 text-white">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Radio className="w-5 h-5 text-green-400 animate-pulse" />
            Ahora mismo
            <span className="text-xs font-normal text-gray-400">(últimos 30 minutos, se actualiza solo)</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-3xl font-bold">{realtime.activeUsers}</p>
              <p className="text-sm text-gray-400">Usuarios activos</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{realtime.pageViews}</p>
              <p className="text-sm text-gray-400">Vistas de página</p>
            </div>
            {realtime.activePages.length > 0 && (
              <div className="col-span-2 md:col-span-2">
                <p className="text-sm text-gray-400 mb-2">Páginas activas ahora</p>
                <div className="space-y-1">
                  {realtime.activePages.slice(0, 3).map((p) => (
                    <div key={p.page} className="flex justify-between text-sm">
                      <span className="truncate mr-4">{p.page || '(sin título)'}</span>
                      <span className="font-semibold">{p.activeUsers}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-3">
        Resumen histórico (últimos 30 días)
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow p-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Usuarios (30 días)</p>
            <p className="text-3xl font-bold text-gray-800 mt-1">{data.totals.activeUsers}</p>
          </div>
          <div className="bg-blue-500 p-3 rounded-full">
            <Users className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Sesiones (30 días)</p>
            <p className="text-3xl font-bold text-gray-800 mt-1">{data.totals.sessions}</p>
          </div>
          <div className="bg-green-500 p-3 rounded-full">
            <MousePointerClick className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Vistas de página (30 días)</p>
            <p className="text-3xl font-bold text-gray-800 mt-1">{data.totals.pageViews}</p>
          </div>
          <div className="bg-purple-500 p-3 rounded-full">
            <Eye className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
      {data.byDay.length === 0 && (
        <p className="text-xs text-gray-400 -mt-6 mb-8">
          Google puede tardar hasta 24-48hs en procesar los datos históricos. El bloque "Ahora mismo" de arriba sí es en vivo.
        </p>
      )}

      {/* Gráfico simple de vistas por día */}
      <div className="bg-white rounded-xl shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-gray-500" />
          Vistas de página por día
        </h2>
        {data.byDay.length === 0 ? (
          <p className="text-gray-500 text-sm">Todavía no hay datos suficientes. Volvé a revisar en unas horas.</p>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex" style={{ minWidth: `${chartMinWidth}px` }}>
              {/* Eje Y */}
              <div className="w-10 flex-shrink-0 flex flex-col justify-between h-48 pr-2 text-xs text-gray-400 text-right">
                <span>{maxDayViews}</span>
                <span>{Math.round(maxDayViews / 2)}</span>
                <span>0</span>
              </div>

              {/* Área del gráfico */}
              <div className="relative flex-1 h-48 border-l border-b border-gray-200">
                {/* Líneas guía horizontales */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                  <div className="border-t border-dashed border-gray-100"></div>
                  <div className="border-t border-dashed border-gray-100"></div>
                  <div></div>
                </div>

                {/* Barras */}
                <div className="absolute inset-0 flex items-end gap-1 px-1">
                  {data.byDay.map((day, index) => (
                    <div
                      key={day.date}
                      className="flex-1 h-full min-w-[6px] flex flex-col items-center justify-end relative"
                      onMouseEnter={() => setHoveredDay(index)}
                      onMouseLeave={() => setHoveredDay(null)}
                    >
                      {hoveredDay === index && (
                        <div className="absolute -top-8 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10 shadow-lg">
                          {formatShortDate(day.date)}: {day.pageViews} {day.pageViews === 1 ? 'vista' : 'vistas'}
                        </div>
                      )}
                      <div
                        className={`w-full rounded-t transition-colors ${
                          hoveredDay === index ? 'bg-blue-600' : 'bg-blue-500'
                        }`}
                        style={{ height: `${Math.max((day.pageViews / maxDayViews) * 100, day.pageViews > 0 ? 3 : 0)}%` }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Etiquetas del eje X */}
            <div className="flex" style={{ minWidth: `${chartMinWidth}px` }}>
              <div className="w-10 flex-shrink-0"></div>
              <div className="flex-1 flex gap-1 px-1 mt-1">
                {data.byDay.map((day, index) => (
                  <div key={day.date} className="flex-1 min-w-[6px] text-center">
                    {(index % labelStep === 0 || index === data.byDay.length - 1) && (
                      <span className="text-[10px] text-gray-400">{formatShortDate(day.date)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Páginas más vistas */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Páginas más vistas</h2>
          {data.topPages.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin datos todavía.</p>
          ) : (
            <div className="space-y-3">
              {data.topPages.map((page) => (
                <div key={page.path} className="flex items-center justify-between text-sm" title={page.path}>
                  <span className="text-gray-700 truncate mr-4">{humanizePagePath(page.path)}</span>
                  <span className="font-semibold text-gray-800 flex-shrink-0">{page.pageViews}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fuentes de tráfico */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">De dónde vienen las visitas</h2>
          {data.sources.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin datos todavía.</p>
          ) : (
            <div className="space-y-3">
              {data.sources.map((source) => (
                <div key={source.channel} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{CHANNEL_LABELS[source.channel] || source.channel}</span>
                  <span className="font-semibold text-gray-800">{source.sessions}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
